import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KeyRound, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        setSent(true);
      } else {
        toast.error(data.detail || 'Something went wrong');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-gradient">
      <Navbar />
      <div className="pt-32 pb-24 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-outfit text-3xl font-semibold text-white mb-2">Reset your password</h1>
            <p className="text-slate-300">Enter your email and we'll send you a reset link</p>
          </div>

          <div className="glass-card p-8">
            {sent ? (
              <div className="text-center space-y-4" data-testid="forgot-password-success">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
                <h2 className="text-xl font-semibold text-white">Check your email</h2>
                <p className="text-slate-300">If an account exists for <span className="text-blue-400">{email}</span>, we've sent a password reset link.</p>
                <p className="text-slate-300 text-sm">Didn't get it? Check your spam folder or try again.</p>
                <Button onClick={() => setSent(false)} variant="outline" className="mt-4" data-testid="try-again-btn">
                  Try another email
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="input-dark pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full btn-primary" data-testid="forgot-submit-btn">
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>
            )}
            <div className="mt-6 text-center">
              <Link to="/login" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-2" data-testid="back-to-login">
                <ArrowLeft className="w-4 h-4" /> Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
